import { v4 as uuidv4 } from "uuid";



import type { IUpdateLocationPspSettingsCmd } from "@/data/commands/location/UpdateLocationPspSettingsCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { PaymentMethodMode, PaymentProvider } from "@/data/model/Enums";
import type { ILocationDetail } from "@/data/model/Location";
import type { ILocationPaymentMethod } from "@/data/model/LocationPaymentMethod";
import { config } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";



import { translate } from "@/shared/localization/language";



import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { LocationPaymentMethodModal } from "@/portal/modal/LocationPaymentMethodModal";


// Apple Pay and Google Pay are only a real wallet payment on a provider that redirects to its own payment page.
// On Stripe they fall back to the regular card form, so these two do not offer Stripe as a provider - paying
// by wallet on Stripe is what the Stripe Checkout payment method is for.
const walletPaymentMethodIds = [config.applePayPaymentMethod, config.googlePayPaymentMethod];
const wallePaymentMethodProviders = [PaymentProvider.Wallee, PaymentProvider.PostFinance];

class LocationPaymentMethodRow {
    locationPaymentMethod: ILocationPaymentMethod;
    rowId: string;

    constructor(locationPaymentMethod: ILocationPaymentMethod) {
        this.locationPaymentMethod = locationPaymentMethod;
        this.rowId = uuidv4();
    }
}

export class LocationPspForm extends BaseEntityForm<ILocationDetail, IUpdateLocationPspSettingsCmd> {
    private dao: LocationDao;
    private locationPaymentMethodRows: Array<LocationPaymentMethodRow> = new Array<LocationPaymentMethodRow>();
    private $newLocationPaymentMethodBtn?: JQuery;
    private locationId?: string;

    constructor(element: JQuery) {
        super(element, {
            getSingle: undefined as any,
            create: undefined as any,
            getData: undefined as any,
            remove: undefined as any,
            update: (i, d) => Factory.getInstance<LocationDao>(LocationDao).setPsp(i, d),
        });

        this.dao = Factory.getInstance<LocationDao>(LocationDao);
    }

    protected resolveErrorMessage(e, error: DaoError): string {
        if (error.type === "location_payment_method_missing_config_exception") {
            const paymentMethodId = error.additionalData.paymentMethodId;
            const merchantIdInput = this.$form.find(`.form-control[name="inputMerchantId${paymentMethodId}"]`);
            const externalPaymentMethodInput = this.$form.find(
                `.form-control[name="inputPaymentMethodId${paymentMethodId}"]`
            );

            externalPaymentMethodInput.addClass("is-invalid");
            merchantIdInput.addClass("is-invalid");

            return i18n("location.psp.missingDefaultPaymentMethodConfiguration", {
                paymentMethodName: error.additionalData.paymentMethodName,
            });
        } else {
            return error.message;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getFormData($form: JQuery): IUpdateLocationPspSettingsCmd {
        let hasError = false;

        // Collect payment methods
        for (const locationPaymentMethodRow of this.locationPaymentMethodRows) {
            const paymentMethodId = locationPaymentMethodRow.rowId;

            const paymentMethodSwitch = new Switch(this.$form.find(`input[name="paymentMethod${paymentMethodId}"]`));

            const paymentProviderSelect = this.$form.find(
                `.form-control[name="selectPaymentMethod${paymentMethodId}"]`
            );
            const merchantIdInput = this.$form.find(`.form-control[name="inputMerchantId${paymentMethodId}"]`);
            const externalPaymentMethodInput = this.$form.find(
                `.form-control[name="inputPaymentMethodId${paymentMethodId}"]`
            );
            const paymentMethodOrderInput = this.$form.find(
                `.form-control[name="inputPaymentMethodOrder${paymentMethodId}"]`
            );

            paymentProviderSelect.parent().removeClass("is-invalid");
            merchantIdInput.removeClass("is-invalid");
            externalPaymentMethodInput.removeClass("is-invalid");

            locationPaymentMethodRow.locationPaymentMethod.isEnabled = paymentMethodSwitch.getValue();

            if (locationPaymentMethodRow.locationPaymentMethod.isEnabled) {
                const externalPaymentId = externalPaymentMethodInput.val()?.trim();
                const merchantId = merchantIdInput.val()?.trim();
                const selectedPaymentMethodProvided =
                    paymentProviderSelect.val() === "" ? undefined : parseInt(paymentProviderSelect.val());
                const order =
                    paymentMethodOrderInput.val() === "" ? undefined : parseInt(paymentMethodOrderInput.val());

                if (
                    locationPaymentMethodRow.locationPaymentMethod.mode === PaymentMethodMode.Online &&
                    (selectedPaymentMethodProvided == null || isNaN(selectedPaymentMethodProvided))
                ) {
                    hasError = true;
                    paymentProviderSelect.parent().addClass("is-invalid");
                } else {
                    if (selectedPaymentMethodProvided === PaymentProvider.Wallee) {
                        if (
                            (externalPaymentId === "" && merchantId != "") ||
                            (externalPaymentId != "" && merchantId === "")
                        ) {
                            hasError = true;
                            externalPaymentMethodInput.addClass("is-invalid");
                            merchantIdInput.addClass("is-invalid");
                        }
                    }

                    if (selectedPaymentMethodProvided === PaymentProvider.PostFinance) {
                        if (externalPaymentId == null || externalPaymentId.length === 0) {
                            hasError = true;
                            externalPaymentMethodInput.addClass("is-invalid");
                        }

                        if (merchantId == null || merchantId.length === 0) {
                            hasError = true;
                            merchantIdInput.addClass("is-invalid");
                        }
                    }

                    if (
                        selectedPaymentMethodProvided === PaymentProvider.Worldline &&
                        locationPaymentMethodRow.locationPaymentMethod.worldlineConfiguration?.useTerminalApi === true
                    ) {
                        if (externalPaymentId == null || externalPaymentId.length === 0) {
                            hasError = true;
                            externalPaymentMethodInput.addClass("is-invalid");
                        }

                        if (merchantId == null || merchantId.length === 0) {
                            hasError = true;
                            merchantIdInput.addClass("is-invalid");
                        }
                    }
                }

                locationPaymentMethodRow.locationPaymentMethod.externalPaymentId =
                    locationPaymentMethodRow.locationPaymentMethod.mode === PaymentMethodMode.Offline
                        ? undefined
                        : externalPaymentId;
                locationPaymentMethodRow.locationPaymentMethod.merchantId =
                    locationPaymentMethodRow.locationPaymentMethod.mode === PaymentMethodMode.Offline
                        ? undefined
                        : merchantId;
                locationPaymentMethodRow.locationPaymentMethod.order = isNaN(order!) ? undefined : order;
                locationPaymentMethodRow.locationPaymentMethod.provider =
                    locationPaymentMethodRow.locationPaymentMethod.mode === PaymentMethodMode.Offline ||
                    selectedPaymentMethodProvided == null
                        ? undefined
                        : selectedPaymentMethodProvided;
            }
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (
            hasError === false
                ? { paymentMethods: this.locationPaymentMethodRows.map((r) => r.locationPaymentMethod) }
                : undefined
        ) as any;
    }

    protected setFormData(model: ILocationDetail | undefined) {
        this.locationId = model!.id;
        this.$newLocationPaymentMethodBtn = $("#new-location-payment-method-btn");
        this.$newLocationPaymentMethodBtn.on("click", () => {
            this.openLocationPaymentMethodModal(undefined);
        });

        this.refreshForm();
    }

    private refreshForm() {
        this.dao.getLocationAvailablePaymentMethods(this.locationId!).then((r) => {
            this.locationPaymentMethodRows = r.map((pm) => new LocationPaymentMethodRow(pm));
            this.refreshPaymentMethodsGrids(this.locationPaymentMethodRows);
        });
    }

    private refreshPaymentMethodsGrids(paymentMethods: Array<LocationPaymentMethodRow>) {
        const onlinePaymentMethodsDiv = $(".location-online-payment-methods-group");
        onlinePaymentMethodsDiv.children().remove();

        // insert online, browser payment methods
        for (const paymentMethod of paymentMethods.filter(
            (i) =>
                i.locationPaymentMethod.mode === PaymentMethodMode.Online &&
                !i.locationPaymentMethod.isPaymentDevice &&
                i.locationPaymentMethod.revenueEffective
        )) {
            this.insertOnlinePaymentMethodRow(paymentMethod, onlinePaymentMethodsDiv);
        }

        // insert online payment device payment methods
        const onlineDevicePaymentMethodsSection = $(".payment-devices-section");
        const onlineDevicePaymentMethodsDiv = $(".location-online-payment-device-methods-group");
        onlineDevicePaymentMethodsDiv.children().remove();

        const devicePayments = paymentMethods.filter(
            (i) =>
                i.locationPaymentMethod.mode === PaymentMethodMode.Online &&
                i.locationPaymentMethod.isPaymentDevice &&
                i.locationPaymentMethod.revenueEffective
        );
        devicePayments.length > 0 ? onlineDevicePaymentMethodsSection.show() : onlineDevicePaymentMethodsSection.hide();

        for (const paymentMethod of paymentMethods.filter(
            (i) =>
                i.locationPaymentMethod.mode === PaymentMethodMode.Online &&
                i.locationPaymentMethod.isPaymentDevice &&
                i.locationPaymentMethod.revenueEffective
        )) {
            this.insertOnlinePaymentMethodRow(paymentMethod, onlineDevicePaymentMethodsDiv);
        }

        // insert offline payment methods, revenue effective
        const offlinePaymentMethodsDiv = $(".location-offline-payment-methods-group");
        offlinePaymentMethodsDiv.children().remove();

        const offlinePaymentMethods = paymentMethods.filter(
            (i) =>
                i.locationPaymentMethod.mode === PaymentMethodMode.Offline && i.locationPaymentMethod.revenueEffective
        );
        for (const paymentMethod of offlinePaymentMethods) {
            this.insertOfflinePaymentMethodRow(paymentMethod, offlinePaymentMethodsDiv);
        }

        // insert not revenue effective payment methods
        const notRevenueEffectivePaymentMethodsSection = $(".not-revenue-effective-payment-methods-section");
        const notRevenueEffectivePaymentMethodsDiv = $(".not-revenue-effective-payment-methods-group");
        notRevenueEffectivePaymentMethodsDiv.children().remove();

        const notRevenueEffectivePaymentMethods = paymentMethods.filter(
            (i) => !i.locationPaymentMethod.revenueEffective
        );
        notRevenueEffectivePaymentMethods.length > 0
            ? notRevenueEffectivePaymentMethodsSection.show()
            : notRevenueEffectivePaymentMethodsSection.hide();

        for (const paymentMethod of notRevenueEffectivePaymentMethods) {
            this.insertOfflinePaymentMethodRow(paymentMethod, notRevenueEffectivePaymentMethodsDiv);
        }
    }

    private insertOnlinePaymentMethodRow(row: LocationPaymentMethodRow, container: JQuery) {
        const paymentMethodId = row.rowId;
        const locationPaymentMethod = row.locationPaymentMethod;

        const paymentMethod = locationPaymentMethod.predefinedPaymentMethod;
        const paymentMethodName =
            paymentMethod == null ? locationPaymentMethod.name : translate(paymentMethod.localizedName);
        const paymentMethodDiv = $('<div class="form-group row" style="align-items: center;"></div').appendTo(
            container
        );
        const logoPath = locationPaymentMethod.logoPath ?? paymentMethod?.imagePath;
        if (logoPath != null && logoPath != "") {
            paymentMethodDiv.append(
                `<label class="col-3 col-form-label" style="display: flex; align-items: center; justify-content: end;"><span>${paymentMethodName}</span><span class="icon" style="background-image:url('${logoPath}');" /></label>`
            );
        } else if (locationPaymentMethod.useCollectiveCardIcon) {
            paymentMethodDiv.append(
                `<label class="col-3 col-form-label" style="display: flex; align-items: center; justify-content: end;"><span>${paymentMethodName}</span><div class="consolidated-icon"><div class="visa"></div><div class="mastercard"></div><div class="plus"><i class="fas fa-plus"></i></div></div></label>`
            );
        } else {
            paymentMethodDiv.append(
                `<label class="col-3 col-form-label" style="display: flex; align-items: center; justify-content: end;"><span>${paymentMethodName}</span></label>`
            );
        }

        paymentMethodDiv.append(
            `<div class="col"><input type="checkbox" name="paymentMethod${paymentMethodId}" /></div>`
        );

        const paymentProviderSelectDiv = $(
            `<div class="col" style="display: flex; margin-right: 8px;"><select class="form-control" name="selectPaymentMethod${paymentMethodId}"></select></div>`
        ).appendTo(paymentMethodDiv);

        const merchantIdInputDiv = $(
            `<div class="col"><input type="text" name="inputMerchantId${paymentMethodId}" class="form-control" placeholder="Merchant Id"/></div>`
        ).appendTo(paymentMethodDiv);
        const paymentMethodIdInputDiv = $(
            `<div class="col"><input type="text" name="inputPaymentMethodId${paymentMethodId}" class="form-control" placeholder="Payment Method Id"/></div>`
        ).appendTo(paymentMethodDiv);
        const paymentMethodOrderInputDiv = $(
            `<div class="col"><input type="number" name="inputPaymentMethodOrder${paymentMethodId}" class="form-control" placeholder="Order"/></div>`
        ).appendTo(paymentMethodDiv);

        if (locationPaymentMethod.isCustom || locationPaymentMethod.isEditEnabled) {
            $(
                '<div style="width: 40px;"><span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span></div>'
            )
                .appendTo(paymentMethodDiv)
                .on("click", () => {
                    this.openLocationPaymentMethodModal(locationPaymentMethod);
                });
        } else {
            $('<div style="width: 40px;"><span></span></div>').appendTo(paymentMethodDiv);
        }

        const paymentProviderSelect = this.$form.find(`.form-control[name="selectPaymentMethod${paymentMethodId}"]`);

        const selectableProviders: Array<number> | undefined = walletPaymentMethodIds.includes(paymentMethod?.id ?? "")
            ? wallePaymentMethodProviders
            : undefined;

        Misc.eachEnum(PaymentProvider, (i) => {
            if (selectableProviders != null && !selectableProviders.includes(i)) return;

            paymentProviderSelect.append('<option value="' + i + '">' + PaymentProvider[i] + "</option>");
        });

        const pps = Select.createFromSelect(paymentProviderSelect);
        pps.onChange = () => {
            updateComponentVisibility();

            const selectedProvider = paymentProviderSelect.val();
            if (
                selectedProvider === PaymentProvider.Wallee.toString() ||
                selectedProvider === PaymentProvider.PostFinance.toString() ||
                (selectedProvider === PaymentProvider.Worldline.toString() && row.locationPaymentMethod.worldlineConfiguration?.useTerminalApi === true)
            ) {
                // Clear inputs
                externalPaymentMethodInput.val("");
                merchantIdInput.val("");
            }
        };

        const merchantIdInput = this.$form.find(`.form-control[name="inputMerchantId${paymentMethodId}"]`);
        const externalPaymentMethodInput = this.$form.find(
            `.form-control[name="inputPaymentMethodId${paymentMethodId}"]`
        );
        const paymentMethodOrderInput = this.$form.find(
            `.form-control[name="inputPaymentMethodOrder${paymentMethodId}"]`
        );

        pps.setValue(locationPaymentMethod.provider);

        pps.setDisabled(!locationPaymentMethod.isProviderEditable);

        merchantIdInput.val(locationPaymentMethod.merchantId!);
        externalPaymentMethodInput.val(locationPaymentMethod.externalPaymentId!);
        paymentMethodOrderInput.val(locationPaymentMethod.order!);

        const paymentMethodSwitch = new Switch(this.$form.find(`input[name="paymentMethod${paymentMethodId}"]`));
        paymentMethodSwitch.setValue(locationPaymentMethod.isEnabled);
        paymentMethodSwitch.onChange = () => {
            updateComponentVisibility();
        };

        const updateComponentVisibility = () => {
            const paymentMethodEnabled = paymentMethodSwitch.getValue();
            const selectedProvider = paymentProviderSelect.val();

            if (paymentMethodEnabled) {
                this.setVisible(paymentProviderSelectDiv);
                this.setVisible(paymentMethodOrderInputDiv);

                if (selectedProvider == null) {
                    this.setHidden(merchantIdInputDiv);
                    this.setHidden(paymentMethodIdInputDiv);
                    this.setHidden(paymentMethodOrderInputDiv);
                } else {
                    if (
                        selectedProvider === PaymentProvider.Stripe.toString() ||
                        (selectedProvider === PaymentProvider.Worldline.toString() && row.locationPaymentMethod.worldlineConfiguration?.useTerminalApi !== true)
                    ) {
                        this.setHidden(merchantIdInputDiv);
                        this.setHidden(paymentMethodIdInputDiv);
                    } else if (
                        selectedProvider === PaymentProvider.Wallee.toString() ||
                        selectedProvider === PaymentProvider.PostFinance.toString() ||
                        selectedProvider === PaymentProvider.PayTec.toString() ||
                        (selectedProvider === PaymentProvider.Worldline.toString() && row.locationPaymentMethod.worldlineConfiguration?.useTerminalApi === true)
                    ) {
                        this.setVisible(merchantIdInputDiv);
                        this.setVisible(paymentMethodIdInputDiv);
                    }
                }
            } else {
                this.setHidden(paymentProviderSelectDiv);
                this.setHidden(merchantIdInputDiv);
                this.setHidden(paymentMethodIdInputDiv);
                this.setHidden(paymentMethodOrderInputDiv);
            }
        };

        updateComponentVisibility();
    }

    private setHidden(object: JQuery) {
        object.css("visibility", "hidden");
    }

    private setVisible(object: JQuery) {
        object.css("visibility", "visible");
    }

    private openLocationPaymentMethodModal(model?: ILocationPaymentMethod) {
        const modal = new LocationPaymentMethodModal(this.id!, model);
        modal.onRemoved = () => {
            this.showSuccess(i18n("general.successfullyRemoved"));
            this.refreshForm();
        };

        modal.onSaved = (m, wasEdited) => {
            this.showSuccess(i18n(wasEdited === true ? "general.successfullyUpdated" : "general.successfullyCreated"));
            this.refreshForm();
        };

        modal.show();
    }

    private insertOfflinePaymentMethodRow(row: LocationPaymentMethodRow, container: JQuery) {
        const paymentMethodId = row.rowId;
        const locationPaymentMethod = row.locationPaymentMethod;

        const predefinedPaymentMethod = locationPaymentMethod.predefinedPaymentMethod;
        const paymentMethodName =
            predefinedPaymentMethod == null
                ? locationPaymentMethod.name
                : translate(predefinedPaymentMethod.localizedName);
        const logoPath =
            predefinedPaymentMethod == null ? locationPaymentMethod.logoPath : predefinedPaymentMethod?.imagePath;
        const paymentMethodDiv = $('<div class="form-group row"></div').appendTo(container);

        paymentMethodDiv.append(
            '<label class="col-3 col-form-label">' +
                paymentMethodName +
                '<span class="icon" style="background-image:url(\'' +
                logoPath +
                "');\" /></label>"
        );
        paymentMethodDiv.append(
            `<div class="col"><input type="checkbox" name="paymentMethod${paymentMethodId}" /></div>`
        );
        paymentMethodDiv.append(`<div class="col"/>`);
        paymentMethodDiv.append(`<div class="col"/>`);
        paymentMethodDiv.append(`<div class="col"/>`);

        const paymentMethodOrderInputDiv = $(
            `<div class="col"><input type="number" name="inputPaymentMethodOrder${paymentMethodId}" class="form-control" placeholder="Order"/></div>`
        ).appendTo(paymentMethodDiv);

        const paymentMethodSwitch = new Switch(this.$form.find(`input[name="paymentMethod${paymentMethodId}"]`));
        paymentMethodSwitch.setValue(locationPaymentMethod.isEnabled);

        if (locationPaymentMethod.isCustom) {
            $(
                '<div style="width: 40px;"><span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span></div>'
            )
                .appendTo(paymentMethodDiv)
                .on("click", () => {
                    this.openLocationPaymentMethodModal(locationPaymentMethod);
                });
        } else {
            $('<div style="width: 40px;"></div>').appendTo(paymentMethodDiv);
        }

        const paymentMethodOrderInput = this.$form.find(
            `.form-control[name="inputPaymentMethodOrder${paymentMethodId}"]`
        );

        paymentMethodOrderInput.val(locationPaymentMethod.order!);
        paymentMethodSwitch.onChange = () => {
            updateComponentVisibility();
        };

        const updateComponentVisibility = () => {
            const paymentMethodEnabled = paymentMethodSwitch.getValue();

            if (paymentMethodEnabled) {
                paymentMethodOrderInputDiv.css("visibility", "visible");
            } else {
                paymentMethodOrderInputDiv.css("visibility", "hidden");
            }
        };

        updateComponentVisibility();
    }
}
